import { call, cancel, fork, put, retry, select, take } from 'typed-redux-saga';

import { FetchError, toLoggableError } from '../../../../../shared/helpers/errors';
import { poll } from '../../../../../shared/helpers/redux-saga';
import { cloudApi } from '../../../../../shared/helpers/transport';
import { CloudAPI } from '../../../../../shared/helpers/transport/cloud-api/types';
import {
    getConferenceConfig,
    selectCanWatchWaitingRoom,
    selectHasNewWaitingRoomPeers,
    selectIsWaitingRoomEnabled,
    selectIsWaitingRoomTab,
    selectSortedWaitingRoomPeers,
    selectWaitingRoomPeers,
} from '../../../selectors';
import { ParticipantID } from '../../../types';
import { updatePermissions } from '../../permissions';
import { logger } from '../helpers/logger';
import { prepareWaitingRoomPeers } from '../helpers/prepare-peers';
import { setHasNewWaitingRoomPeers, setWaitingRoomPeers, showWaitingRoomNotificationsAction } from '../slice';

export function* watchWaitingRoomPollingSaga() {
    let lastTask = null;
    let canWatchWaitingRoom = false;

    while (true) {
        yield* take(updatePermissions);

        const newCanWatchWaitingRoom = yield* select(selectCanWatchWaitingRoom);

        if (canWatchWaitingRoom === newCanWatchWaitingRoom) {
            return;
        }

        canWatchWaitingRoom = newCanWatchWaitingRoom;

        if (lastTask) {
            yield* cancel(lastTask);
            lastTask = null;
        }

        if (canWatchWaitingRoom) {
            yield* call(waitingRoomUpdateSaga);
            lastTask = yield* fork(peersPollingSaga);
        }
    }
}

export function* peersPollingSaga() {
    const conferenceConfig = yield* select(getConferenceConfig);
    const pollInterval = conferenceConfig?.client_configuration?.waiting_room_peers_refresh_interval_ms || 10000;
    const fiveHundredErrorsCounter = { value: 0 };

    function* handlePeersPollingErrorsSaga(error: unknown) {
        logger.error(error, {
            method: 'handlePeersPollingErrorsSaga',
        });
        const isTerminatingError = yield* call(handlePeersTerminatingFetchError, error);
        if (FetchError.is(error) && error.status >= 500) {
            fiveHundredErrorsCounter.value += 1;
        }
        const isRetryLimitReached = fiveHundredErrorsCounter.value > 2;

        return isTerminatingError || isRetryLimitReached;
    }

    try {
        yield* poll(
            {
                pollingInterval: pollInterval,
                onSuccess: function () {
                    fiveHundredErrorsCounter.value = 0;
                    return false;
                },
                onError: function* (error) {
                    return yield* call(handlePeersPollingErrorsSaga, error);
                },
            },
            updatePeersSaga
        );
    } catch (error) {
        logger.error(toLoggableError(error), {
            method: 'peersPollingSaga',
        });
    }
}

export function* waitingRoomUpdateSaga() {
    const isWaitingRoomEnabled = yield* select(selectIsWaitingRoomEnabled);

    if (!isWaitingRoomEnabled) {
        return;
    }

    try {
        const canWatchWaitingRoom = yield* select(selectCanWatchWaitingRoom);
        if (!canWatchWaitingRoom) {
            return;
        }
        yield* retry(3, 5000, updatePeersSaga);

        const peers = yield* select(selectSortedWaitingRoomPeers);

        if (peers.length === 0) {
            yield* put(setHasNewWaitingRoomPeers(false));
        }
        yield* put(showWaitingRoomNotificationsAction());
    } catch (error) {
        logger.error(
            toLoggableError(error, {
                method: 'waitingRoomUpdateSaga',
            })
        );
    }
}

function* setWaitingRoomPeersSaga(peers: CloudAPI.WaitingRoomPeerState[]) {
    const newWaitingRoomPeers = prepareWaitingRoomPeers(peers);
    const waitingRoomPeers = yield* select(selectWaitingRoomPeers);
    const currentIds = waitingRoomPeers.reduce(
        (result, { id }) => {
            result[id] = true;
            return result;
        },
        {} as Record<ParticipantID, boolean>
    );
    const isWaitingRoomTab = yield* select(selectIsWaitingRoomTab);
    const hasNewWaitingRoomPeers = yield* select(selectHasNewWaitingRoomPeers);
    yield* put(
        setHasNewWaitingRoomPeers(
            hasNewWaitingRoomPeers || (!isWaitingRoomTab && newWaitingRoomPeers.some(({ id }) => !currentIds[id]))
        )
    );
    yield* put(setWaitingRoomPeers(newWaitingRoomPeers));
}
function* updatePeersSaga() {
    try {
        const conferenceConfig = yield* select(getConferenceConfig);
        if (!conferenceConfig) {
            return;
        }

        const { peers } = yield* call([cloudApi, cloudApi.getWaitingRoomPeers], conferenceConfig.resolved_uri);
        yield* call(setWaitingRoomPeersSaga, peers);
    } catch (error) {
        logger.error(
            toLoggableError(error, {
                method: 'updatePeersSaga',
            })
        );
        throw error;
    }
}

function handlePeersTerminatingFetchError(error: unknown) {
    if (FetchError.is(error)) {
        if (error.isClientError()) {
            return true;
        }
    }
    return false;
}
